import React, { useState } from "react";
import ChatAttachment from "@/components/chat/ChatAttachment";
import { SmilePlus, Pin, PinOff, Pencil, Trash2, MessageSquare, Check, X, Ticket, CheckCheck } from "lucide-react";

const QUICK = ["👍", "✅", "👀", "❤️", "🎉"];

const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

function renderBody(body, members) {
  const text = body || "";
  const names = (members || []).map((m) => m.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (names.length) {
    const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const rx = new RegExp(`(@(?:${names.map(esc).join("|")}))`, "g");
    const parts = text.split(rx);
    return parts.map((p, i) =>
      p.startsWith("@") && names.includes(p.slice(1))
        ? <span key={i} className="text-[#B8873A] font-semibold">{p}</span>
        : <span key={i}>{p}</span>
    );
  }
  const parts = text.split(/(@[^\s@]+)/g);
  return parts.map((p, i) =>
    p.startsWith("@") ? <span key={i} className="text-[#B8873A] font-semibold">{p}</span> : <span key={i}>{p}</span>
  );
}

export default function ChatMessage({ m, user, isAdmin, members, canPost = true, onReact, onPin, onUnpin, onEdit, onDelete, onOpenThread, onToTicket, onReceipts, inThread }) {
  const own = m.author_id === user?.id;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.body || "");
  const [showPicker, setShowPicker] = useState(false);
  const canEdit = own && !m.deleted;
  const canDelete = (own || isAdmin) && !m.deleted;

  const saveEdit = () => { if (draft.trim()) { onEdit(m, draft.trim()); setEditing(false); } };

  if (m.deleted) {
    return (
      <div className="flex justify-start" data-testid={`chat-message-${m.id}`}>
        <div className="text-xs text-slate-400 italic px-3.5 py-1.5">Message supprimé</div>
      </div>
    );
  }

  const reactions = m.reactions || {};
  const hasReactions = Object.keys(reactions).length > 0;

  return (
    <div className={`group flex ${own ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.id}`}>
      <div className={`max-w-[72%] flex flex-col ${own ? "items-end" : "items-start"}`}>
        <div className="flex items-center gap-2 mb-0.5">
          {!own && <span className="text-xs font-semibold text-slate-700">{m.author_name}</span>}
          <span className="text-[10px] text-slate-400">{fmtTime(m.created_at)}</span>
          {m.pinned && <Pin className="w-3 h-3 text-[#B8873A]" />}
          {m.edited_at && <span className="text-[10px] text-slate-300 italic">modifié</span>}
        </div>

        <div className="relative">
          {/* Toolbar */}
          <div className={`absolute -top-3.5 ${own ? "right-0" : "left-0"} opacity-0 group-hover:opacity-100 transition-opacity z-10 flex items-center gap-0.5 bg-white border border-slate-200 rounded-full shadow-sm px-1 py-0.5`} data-testid={`msg-toolbar-${m.id}`}>
            <button onClick={() => setShowPicker((v) => !v)} title="Réagir" data-testid={`msg-react-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><SmilePlus className="w-3.5 h-3.5" /></button>
            {!inThread && <button onClick={() => onOpenThread(m)} title="Répondre dans un fil" data-testid={`msg-thread-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><MessageSquare className="w-3.5 h-3.5" /></button>}
            {onToTicket && <button onClick={() => onToTicket(m)} title="Convertir en ticket" data-testid={`msg-to-ticket-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><Ticket className="w-3.5 h-3.5" /></button>}
            {onReceipts && (own || isAdmin) && <button onClick={() => onReceipts(m)} title="Accusés de lecture" data-testid={`msg-receipts-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><CheckCheck className="w-3.5 h-3.5" /></button>}
            {m.pinned
              ? <button onClick={() => onUnpin(m)} title="Désépingler" data-testid={`msg-unpin-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><PinOff className="w-3.5 h-3.5" /></button>
              : (canPost && <button onClick={() => onPin(m)} title="Épingler" data-testid={`msg-pin-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><Pin className="w-3.5 h-3.5" /></button>)}
            {canEdit && <button onClick={() => { setEditing(true); setDraft(m.body || ""); }} title="Modifier" data-testid={`msg-edit-btn-${m.id}`} className="p-1 text-slate-400 hover:text-[#B8873A]"><Pencil className="w-3.5 h-3.5" /></button>}
            {canDelete && <button onClick={() => onDelete(m)} title="Supprimer" data-testid={`msg-delete-btn-${m.id}`} className="p-1 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>

          {showPicker && (
            <div className={`absolute -top-9 ${own ? "right-0" : "left-0"} z-20 flex gap-1 bg-white border border-slate-200 rounded-full shadow-md px-2 py-1`} data-testid={`msg-react-picker-${m.id}`}>
              {QUICK.map((e) => (
                <button key={e} onClick={() => { onReact(m, e); setShowPicker(false); }} className="text-base hover:scale-125 transition-transform">{e}</button>
              ))}
            </div>
          )}

          <div className={`rounded-lg px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${own ? "bg-[#111111] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
            {editing ? (
              <div className="flex flex-col gap-1.5 min-w-[220px]">
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} data-testid={`msg-edit-input-${m.id}`}
                  className="w-full text-sm text-slate-800 border border-slate-300 rounded-sm px-2 py-1 focus:outline-none" />
                <div className="flex gap-1 justify-end">
                  <button onClick={() => setEditing(false)} className="p-1 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
                  <button onClick={saveEdit} data-testid={`msg-edit-save-${m.id}`} className="p-1 text-emerald-600 hover:text-emerald-800"><Check className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <>
                {m.body && <div>{renderBody(m.body, members)}</div>}
                {(m.attachments || []).map((a) => <ChatAttachment key={a.file_id} att={a} />)}
              </>
            )}
          </div>
        </div>

        {hasReactions && (
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(reactions).map(([emoji, users]) => {
              const mine = (users || []).includes(user?.id);
              return (
                <button key={emoji} onClick={() => onReact(m, emoji)} data-testid={`msg-reaction-${m.id}-${emoji}`}
                  className={`text-xs px-1.5 py-0.5 rounded-full border flex items-center gap-1 transition-colors ${mine ? "bg-[#B8873A]/10 border-[#B8873A] text-[#8a6628]" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  <span>{emoji}</span><span>{(users || []).length}</span>
                </button>
              );
            })}
          </div>
        )}

        {!inThread && m.reply_count > 0 && (
          <button onClick={() => onOpenThread(m)} data-testid={`msg-replies-${m.id}`}
            className="mt-1 text-[11px] text-[#B8873A] font-semibold hover:underline flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> {m.reply_count} réponse{m.reply_count > 1 ? "s" : ""}
          </button>
        )}
      </div>
    </div>
  );
}
